import React, { useEffect, useState } from 'react';
import { X, Loader2, Download } from 'lucide-react';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Button } from '../../ui/button';
import api from '../../../utils/api';
import { downloadCrmMailAttachment } from '../../../utils/crmMailDownload';
import { useToast } from '../../../hooks/use-toast';

export default function CrmMailAttachmentPreview({ attachment, onClose }) {
  const { toast } = useToast();
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const isPdf = attachment?.mime_type === 'application/pdf';
  const isImage = attachment?.mime_type?.startsWith('image/');

  useEffect(() => {
    let objectUrl = null;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/crm/mail/attachments/${attachment.id}/preview`, {
          responseType: 'blob',
        });
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      } catch {
        setUrl(null);
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment?.id]);

  const handleDownload = async () => {
    try {
      await downloadCrmMailAttachment(attachment.id, attachment.filename);
    } catch (e) {
      toast({
        title: 'Download failed',
        description: e?.message || 'Could not download attachment',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b">
          <span className="text-sm font-medium truncate">{attachment?.filename}</span>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={handleDownload} title="Download">
              <Download className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-center min-h-[400px] bg-muted/30 p-4">
          {loading && <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />}
          {!loading && url && isImage && (
            <img src={url} alt={attachment.filename} className="max-w-full max-h-[70vh] object-contain" />
          )}
          {!loading && url && isPdf && (
            <iframe src={url} title={attachment.filename} className="w-full h-[70vh] border-0" />
          )}
          {!loading && !url && (
            <p className="text-muted-foreground">Preview not available</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
